/**
 * Analytics event name constants
 *
 * This file contains standardized event names and categories for Plausible Analytics.
 * Use these constants when tracking events to ensure consistency.
 */

import { isBrowser } from "./ssrUtils";

// Page view events
export const PAGE_VIEW = "pageview";
export const PAGE_NOT_FOUND = "404";

// User interaction events
export const BUTTON_CLICK = "Button Click";
export const LINK_CLICK = "Link Click";
export const FORM_SUBMIT = "Form Submit";
export const VIDEO_PLAY = "Video Play";
export const VIDEO_PAUSE = "Video Pause";
export const VIDEO_COMPLETE = "Video Complete";

// Specific action events
export const NEWSLETTER_SUBSCRIBE = "Newsletter Subscription";
export const DISCORD_JOIN = "Discord Join";
export const FEATURE_EXPLORE = "Feature Explore";

// Premium-specific events
export const PREMIUM_PAGE_VIEW = "Premium Page Viewed";
export const JOIN_PREMIUM = "Join Premium";
export const PREMIUM_TRIAL_START = "Premium Trial Start";
export const PREMIUM_LEARN_MORE = "Premium Learn More";
export const PRICING_PLAN_VIEW = "Pricing Plan Viewed";
export const PREMIUM_FEATURE_INTERACT = "Premium Feature Interact";

// Generic button type events (for totals)
export const INVITE_BOT = "Invite Bot";
export const LAUNCH_ACTIVITY = "Launch Activity";
export const OPEN_WEB_APP = "Open Web App";
export const DOWNLOAD_APP = "Download App";
export const DOCS_VISIT = "Docs Visit";
export const PREMIUM_VISIT = "Premium Visit";

// Specific button location events
export const INVITE_BOT_HERO = "Invite Bot - Hero";
export const INVITE_BOT_HEADER = "Invite Bot - Header";
export const INVITE_BOT_FEATURE = "Invite Bot - Feature Card";
export const LAUNCH_ACTIVITY_FEATURE = "Launch Activity - Feature Card";
export const OPEN_WEB_APP_FEATURE = "Open Web App - Feature Card";
export const DOWNLOAD_APP_FEATURE = "Download App - Feature Card";
export const DOWNLOAD_APP_HEADER = "Download App - Header";
export const DOCS_VISIT_HEADER = "Docs Visit - Header";
export const PREMIUM_VISIT_HEADER = "Premium Visit - Header";

// Premium-specific button location events
export const JOIN_PREMIUM_PAGE = "Join Premium - Premium Page";
export const JOIN_PREMIUM_SECTION = "Join Premium - Premium Section";
export const PREMIUM_TRIAL_SECTION = "Premium Trial - Premium Section";
export const PREMIUM_LEARN_MORE_SECTION =
  "Premium Learn More - Premium Section";

// Categories (Legacy - kept for existing usage in Download.tsx and MainScene.tsx)
export const CATEGORIES = {
  HERO: "Hero Section",
  STORY: "Story Section",
  FEATURES: "Features Section",
  FOOTER: "Footer",
  NAV: "Navigation",
  DOWNLOAD: "Download",
};

/**
 * Helper function to create standardized props for Plausible events
 * Legacy function - kept for existing usage in Download.tsx and MainScene.tsx
 * For new implementations, consider using dual event tracking with trackButtonClick
 */
export const createEventProps = (
  category: string,
  action?: string,
  label?: string
) => {
  const props: Record<string, string> = {
    category,
  };

  if (action) props.action = action;
  if (label) props.label = label;

  return { props };
};

/**
 * Helper function to track both generic and specific button events
 * Fires two events: one for totals and one for specific button location tracking
 * Uses navigator.sendBeacon for reliable tracking when available
 */
export const trackButtonClick = (
  trackEventFunc: (
    eventName: string,
    props?: { props?: Record<string, string | number> }
  ) => void,
  genericEvent: string,
  specificEvent: string,
  callback?: () => void
) => {
  // Validate that both events are provided
  if (!genericEvent || !specificEvent) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "Analytics: Both genericEvent and specificEvent are required for trackButtonClick"
      );
    }
    callback?.();
    return;
  }

  // Validate that events are non-empty strings
  if (typeof genericEvent !== "string" || typeof specificEvent !== "string") {
    if (process.env.NODE_ENV === "development") {
      console.warn("Analytics: Both events must be valid strings");
    }
    callback?.();
    return;
  }

  try {
    // Track generic event for totals (e.g., "Invite Bot")
    trackEventFunc(genericEvent);

    // Track specific event for individual button location (e.g., "Invite Bot - Hero")
    trackEventFunc(specificEvent);

    // Execute callback after tracking (for navigation)
    callback?.();
  } catch (error) {
    // Silently fail in production - analytics failures shouldn't break user experience
    // Only log in development for debugging
    if (process.env.NODE_ENV === "development" && isBrowser()) {
      console.error("Analytics: Failed to track button click events", error);
    }
    // Still execute callback even if tracking fails
    callback?.();
  }
};

/**
 * Helper function for tracking button clicks with reliable navigation
 * Prevents default navigation, tracks events, then navigates via callback
 */
export const trackButtonClickWithNavigation = (
  trackEventFunc: (
    eventName: string,
    props?: { props?: Record<string, string | number> }
  ) => void,
  genericEvent: string,
  specificEvent: string,
  targetUrl: string,
  openInNewTab: boolean = false
) => {
  return (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    trackButtonClick(trackEventFunc, genericEvent, specificEvent, () => {
      // Use a small delay to ensure tracking completes
      setTimeout(() => {
        if (openInNewTab) {
          window.open(targetUrl, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = targetUrl;
        }
      }, 100);
    });
  };
};
